import { Button, Flex, Input, Radio, Space } from "antd";
import { useState, useRef, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import Webcam from "react-webcam";

import frontGrid4PhotoImg from "../images/grid_4photos.png";
import frontGridAAIIImg from "../images/grid_aaii.png";
import frontGridVisionImg from "../images/grid_vision.png";
import frontGridFukudaImg from "../images/fukuda_grid.png";
import frontGridElicoidaleImg from "../images/grid_centerted.png";
import {
  EXAM_4PHOTOS_SLUGNAME,
  EXAM_AAII_SLUGNAME,
  EXAM_BALANCE_SLUGNAME,
  EXAM_MAGNO_SLUGNAME,
  EXAM_PODO_SLUGNAME,
  EXAM_PODO_STIM_SLUGNAME,
  EXAM_VISION_SLUGNAME,
  EXAM_HEAD_SLUGNAME,
  EXAM_FUKUDA_SLUGNAME,
  EXAM_ADAMS_SLUGNAME,
  EXAM_ROMBERG_SLUGNAME,
  EXAM_ANAM_SLUGNAME,
  EXAM_ELICOIDALE_SLUGNAME,
} from "./exams_definitions_utils";
import { COOKIE_WC_HEIGHT } from "./exam_const";

const getGridByExamType = (examType: string) => {
  switch (examType) {
    case EXAM_4PHOTOS_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_AAII_SLUGNAME:
      return frontGridAAIIImg;
    case EXAM_PODO_SLUGNAME:
      return "";
    case EXAM_PODO_STIM_SLUGNAME:
      return "";
    case EXAM_VISION_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_MAGNO_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_HEAD_SLUGNAME:
      return frontGridVisionImg;
    case EXAM_ADAMS_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_ROMBERG_SLUGNAME:
      return frontGrid4PhotoImg;
    case EXAM_FUKUDA_SLUGNAME:
      return frontGridFukudaImg;
    case EXAM_BALANCE_SLUGNAME:
      return "";
    case EXAM_ANAM_SLUGNAME:
      return "";
    case EXAM_ELICOIDALE_SLUGNAME:
      return frontGridElicoidaleImg;
    default:
      return "";
  }
};

const gridOptions = [
  { label: "Griglia", value: "grid" },
  { label: "Vuoto", value: "" },
];

const PosturalExamWebcam = ({
  takePhoto,
  posturalExamType,
  setTakePhoto,
  insertPhoto,
}: {
  takePhoto: number;
  posturalExamType: string;
  setTakePhoto: Function;
  insertPhoto: Function;
}) => {
  const [deviceId, setDeviceId] = useState<string>("");
  const [devicePodo, setDevicePodo] = useState<string>(
    "http://192.168.0.159:82/stream"
  );
  const [deviceCam, setDeviceCam] = useState<string>("");

  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [isVisibleWebCam, setisVisibleWebCam] = useState(false);

  const [grid, setGrid] = useState<boolean>(true);
  const [cookies, setCookie] = useCookies([COOKIE_WC_HEIGHT]);
  const [refreshIntervalHandler, setRefreshIntervalHandler] =
    useState<NodeJS.Timer>();
  const webcamRef = useRef<Webcam>(null);
  const [webcamHeight, setWebcamHeight] = useState<number>(
    cookies[COOKIE_WC_HEIGHT] ? parseInt(cookies[COOKIE_WC_HEIGHT]) : 448
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);
  const frontGridRef = useRef<HTMLImageElement>(null);

  // Stato per attivare/disattivare l'effetto specchio
  const [isMirror, setIsMirror] = useState<boolean>(
    posturalExamType === EXAM_PODO_SLUGNAME || posturalExamType === EXAM_PODO_STIM_SLUGNAME
  );
  // Utilizzo di un ref per avere sempre il valore aggiornato di isMirror
  const isMirrorRef = useRef(isMirror);
  useEffect(() => {
    isMirrorRef.current = isMirror;
  }, [isMirror]);

  const handleDevices = useCallback(
    (mediaDevices: MediaDeviceInfo[]) => {
      const webcamDevices = mediaDevices.filter(
        ({ kind }: { kind: string }) => kind === "videoinput"
      );
      if (webcamDevices.length > 0) {
        setDevices(webcamDevices);
        setDeviceId(webcamDevices[0].deviceId);
      }
    },
    [setDevices]
  );

  useEffect(() => {
    if (
      navigator &&
      navigator.mediaDevices &&
      navigator.mediaDevices.enumerateDevices
    )
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const [width, setWidth] = useState(0);
  const handleResize = () => setWidth(window.innerWidth);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    setWidth(window.innerWidth);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const rotate270: boolean =
    (width <= 640 &&
      posturalExamType !== EXAM_PODO_SLUGNAME &&
      posturalExamType !== EXAM_PODO_STIM_SLUGNAME) ||
    (width > 640 && posturalExamType === EXAM_PODO_SLUGNAME) ||
    (width > 640 && posturalExamType === EXAM_PODO_STIM_SLUGNAME);

  const useGrid: boolean =
    posturalExamType !== EXAM_PODO_SLUGNAME &&
    posturalExamType !== EXAM_PODO_STIM_SLUGNAME;

  const allRotate: boolean = posturalExamType !== EXAM_ANAM_SLUGNAME;

  useEffect(() => {
    if (hiddenCanvasRef.current && takePhoto >= 0)
      hiddenCanvasRef.current.toBlob(
        (blob: Blob | null) => {
          insertPhoto(
            hiddenCanvasRef.current ? hiddenCanvasRef.current.toDataURL() : "",
            blob,
            takePhoto
          );
          setTakePhoto(-1);
        },
        "image/png",
        1
      );
  }, [takePhoto]);

  const OpenImageWebCam = () => setisVisibleWebCam(!isVisibleWebCam);

  // Imposta l'intervallo di aggiornamento del canvas includendo grid nel dependency array.
  useEffect(() => {
    if (refreshIntervalHandler) clearInterval(refreshIntervalHandler);
    const interval = setInterval(() => drawImg(grid), 60);
    setRefreshIntervalHandler(interval);
    return () => clearInterval(interval);
  }, [grid]); // isMirror viene aggiornato tramite il ref

  function mirrorChange() {
    setIsMirror((prev) => !prev);
  }

  // Aggiorna il canvas ogni volta che isMirror o grid cambia
  useEffect(() => {
    drawImg(grid);
  }, [isMirror, grid]);

  function drawImg(drawGrid: boolean) {
    if (
      webcamRef.current &&
      canvasRef.current &&
      hiddenCanvasRef.current &&
      frontGridRef.current
    ) {
      const video: Webcam = webcamRef.current;
      const canvas: HTMLCanvasElement = canvasRef.current;
      const hiddenCanvas: HTMLCanvasElement = hiddenCanvasRef.current;
      const frontGrid: HTMLImageElement = frontGridRef.current;

      if (video != null && canvas != null && hiddenCanvas) {
        const ctx = canvas.getContext("2d");
        const hiddenCtx = hiddenCanvas.getContext("2d");

        if (video.video != null && ctx != null && hiddenCtx != null) {
          // Imposta le dimensioni dei canvas in base a rotate270
          canvas.height = rotate270
            ? video.video.videoWidth
            : video.video.videoHeight;
          canvas.width = rotate270
            ? video.video.videoHeight
            : video.video.videoWidth;

          hiddenCanvas.width = rotate270
            ? video.video.videoHeight
            : video.video.videoWidth;
          hiddenCanvas.height = rotate270
            ? video.video.videoWidth
            : video.video.videoHeight;

          // Pulisce i canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          hiddenCtx.clearRect(0, 0, hiddenCanvas.width, hiddenCanvas.height);

          if (rotate270) {
            // Salva lo stato del contesto per poterlo ripristinare
            ctx.save();
            hiddenCtx.save();

            // Applica prima la traslazione e la rotazione (equivalente a 270° o -90°)
            ctx.translate(0, canvas.height);
            ctx.rotate(-90 * Math.PI / 180);
            hiddenCtx.translate(0, hiddenCanvas.height);
            hiddenCtx.rotate(-90 * Math.PI / 180);

            // Se il mirror è abilitato (prendendo il valore dal ref aggiornato)
            if (isMirrorRef.current) {
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);

              hiddenCtx.translate(hiddenCanvas.width, 0);
              hiddenCtx.scale(-1, 1);
            }

            // Disegna il video
            ctx.drawImage(video.video, 0, 0, canvas.height, canvas.width);
            hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.height, hiddenCanvas.width);

            // Ripristina il contesto
            ctx.restore();
            hiddenCtx.restore();

            // Disegna anche la griglia se richiesta, applicando le stesse trasformazioni
            if (frontGrid != null && drawGrid) {
              ctx.save();
              ctx.translate(0, canvas.height);
              ctx.rotate(-90 * Math.PI / 180);
              if (isMirrorRef.current) {
                ctx.translate(canvas.width, 0);
                ctx.scale(-1, 1);
              }
              ctx.drawImage(frontGrid, 0, 0, canvas.height, canvas.width);
              ctx.restore();
            }
          } else {
            // Ramo senza rotazione a 270°
            if (isMirrorRef.current) {
              ctx.translate(canvas.width, 0);
              ctx.scale(-1, 1);
              ctx.drawImage(video.video, 0, 0, canvas.width, canvas.height);

              hiddenCtx.translate(hiddenCanvas.width, 0);
              hiddenCtx.scale(-1, 1);
              hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.width, hiddenCanvas.height);
            } else {
              ctx.drawImage(video.video, 0, 0, canvas.width, canvas.height);
              hiddenCtx.drawImage(video.video, 0, 0, hiddenCanvas.width, hiddenCanvas.height);
            }

            // Disegna la griglia se richiesta
            if (frontGrid != null && drawGrid) {
              ctx.drawImage(frontGrid, 0, 0, canvas.width, canvas.height);
            }
          }
        }
      }
    }
  }

  return (
    <>
      {width > 640 && (
        // Big Screen Mode
        <>
          <div style={{ zoom: "1" }}>
            <Flex justify={"space-evenly"} align={"center"}>
              <Flex align="center">
              <div style={{ paddingLeft: "100px" }}></div>
                <Space size="small">
                  <div >
                  <label>Webcam selezionata: </label> 
                  <select
                    onChange={(e) => setDeviceId(e.target.value)}
                    style={{ marginRight: "10px" }}
                  >
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  </div>
                </Space>
              </Flex>
              <Flex align="center">
                <Space size="small">
                  <div>
                  <label>Dimensione immagine: </label>
                  <Input
                    className="webbo"
                    defaultValue={webcamHeight}
                    aria-label="Dimensione immagine"
                    type="range"
                    min={400}
                    step={50}
                    max={1200}
                    onChange={(e) => {
                      setWebcamHeight(parseInt(e.target.value));
                      setCookie(COOKIE_WC_HEIGHT, e.target.value, {
                        path: "/",
                        expires: new Date(
                          new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5
                        ),
                      });
                    }}
                  />
                  </div>
                </Space>
              </Flex>
            </Flex>
          </div>
          <div className="centered">
            <img
              ref={frontGridRef}
              style={{ display: "none" }}
              src={getGridByExamType(posturalExamType)}
            />
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotQuality={1.0}
              mirrored={false}
              videoConstraints={{
                height: webcamHeight,
                width: Math.round(webcamHeight * 1.48),
                deviceId: deviceId,
              }}
              style={{
                width: "0%",
                height: "0%",
              }}
            />
            <canvas className="webb" ref={canvasRef} />
            <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
          </div>

          <div className="centered" style={{ width: "50%" }}>
            {useGrid && (
              <Radio.Group
                className="exam-image-btn"
                options={gridOptions}
                onChange={(e) => setGrid(e.target.value == "grid")}
                defaultValue={"grid"}
                optionType="button"
                buttonStyle="solid"
              />
            )}

            {(posturalExamType === EXAM_PODO_SLUGNAME ||
              posturalExamType === EXAM_PODO_STIM_SLUGNAME) && (
              <Button onClick={mirrorChange}>Inverti</Button>
            )}
          </div>
        </>
      )}

      {width <= 640 && (
        // Mobile Mode
        <>
          <Button
            onClick={OpenImageWebCam}
            style={{ width: "50%", margin: "auto", marginBottom: "10px" }}
          >
            Posizionamento
          </Button>

          {isVisibleWebCam && (
            <>
              <div>
                <div style={{ display: "none" }}>
                  <select
                    onChange={(e) => setDeviceId(e.target.value)}
                    style={{ marginRight: "16px" }}
                  >
                    {devices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  {useGrid && (
                    <Radio.Group
                      className="exam-image-btn"
                      options={gridOptions}
                      onChange={(e) => setGrid(e.target.value == "grid")}
                      defaultValue={"grid"}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  )}
                </div>
                <div className="centered" style={{ width: "50%" }}>
                  <div className="webbolos">
                    <Input
                      style={{ display: "none" }}
                      className="webbo"
                      defaultValue={webcamHeight}
                      type="range"
                      min={500}
                      step={50}
                      max={1200}
                      onChange={(e) => {
                        setWebcamHeight(parseInt(e.target.value));
                        setCookie(COOKIE_WC_HEIGHT, e.target.value, {
                          path: "/",
                          expires: new Date(
                            new Date().getTime() + 1000 * 60 * 60 * 24 * 365 * 5
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="centered">
                <img
                  ref={frontGridRef}
                  style={{ display: "none" }}
                  src={getGridByExamType(posturalExamType)}
                />
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotQuality={1.0}
                  mirrored={false}
                  videoConstraints={{
                    height: webcamHeight,
                    width: Math.round(webcamHeight * 1.48),
                    deviceId: deviceId,
                    facingMode: "environment",
                  }}
                  style={{
                    width: "0%",
                    height: "0%",
                  }}
                />
                <canvas
                  style={{
                    position: "fixed",
                    width: "100vw",
                    zIndex: "99999999",
                    marginBottom: "25px",
                    left: "0",
                  }}
                  ref={canvasRef}
                />
                <canvas
                  ref={hiddenCanvasRef}
                  style={{
                    display: "none",
                  }}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default PosturalExamWebcam;
